import { ActionDialog } from '@/components/Focus/ActionDialog';
import { PolarisIcon } from '@/exports/columnCommonComponents';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectAllLanguagesMap } from '@/redux/selectors/constantsSelectors';
import { selectFocusNewScheduleSlice, selectFocusRightsPossibleById } from '@/redux/selectors/focusNewScheduleSelectors';
import { selectVersions } from '@/redux/selectors/orderPlacementSelectors';
import {
  ensureScheduleGroupsInitialized,
  getFocusRightsPossible,
  getFocusRightsValidateById,
  setScheduleData
} from '@/redux/slices/focusNewScheduleSlice';
import { removeVersionSelection } from '@/redux/slices/orderPlacementSlice';
import styled from '@emotion/styled';
import { Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import ReviewScheduleDeleteDialogSummary, { DeleteDialogScheduleItem } from './ReviewScheduleDeleteDialogSummary';
import ReviewScheduleListAccordion, { ScheduleRow } from './ReviewScheduleListAccordion';
import NewScheduleAvailSelectionModal from '../NewScheduleAvails/NewScheduleAvailSelectionModal';
import { defaultAvailEndDate } from '@/components/Focus/constants';
import ReviewScheduleAccordionHeader from './ReviewScheduleAccordionHeader';
import { formatDateTimePartsLocal, joinOrDash } from '@/components/Focus/utils/newScheduleAvails';
import { ScheduleAvailType, ScheduleAvailRights } from '@/redux/slices/slice_types';

type ScheduleGroup = ScheduleRow[];

type ReviewScheduleProps = {
  onIncrementalLaunchWorkspaceOpenChange?: (isOpen: boolean) => void;
};

const ReviewSchedule = ({ onIncrementalLaunchWorkspaceOpenChange }: ReviewScheduleProps) => {
  const dispatch = useAppDispatch();
  const selectedVersionsUnSorted = useAppSelector(selectVersions);
  const allLanguagesMap = useAppSelector(selectAllLanguagesMap);

  const {
    frequency,
    startDate,
    endDate,
    selectedTerritories,
    selectedDubs,
    selectedSubs,
    selectedLicense,
    selectedPlatforms,
    hasReviewChanges,
    scheduleGroups,
    selectedVersions: selectedVersionIds
  } = useAppSelector(selectFocusNewScheduleSlice);

  const regionNamesInEnglish = useMemo(() => new Intl.DisplayNames(['en'], { type: 'region' }), []);

  const selectedVersions = useMemo(() => {
    if (!selectedVersionsUnSorted || selectedVersionsUnSorted.length === 0) return [];

    return [...selectedVersionsUnSorted].sort((a, b) => {
      const sequenceOrderA = a.lineage_meta?.episode_sequence_order || a.lineage_meta?.ova_sequence_order || a.sequence_order || 0;
      const sequenceOrderB = b.lineage_meta?.episode_sequence_order || b.lineage_meta?.ova_sequence_order || b.sequence_order || 0;
      return sequenceOrderA - sequenceOrderB;
    });
  }, [selectedVersionsUnSorted]);

  useEffect(() => {
    dispatch(ensureScheduleGroupsInitialized());
  }, [
    dispatch,
    selectedVersionsUnSorted,
    frequency,
    startDate,
    endDate,
    selectedLicense,
    selectedPlatforms,
    selectedTerritories,
    selectedDubs,
    selectedSubs
  ]);

  const [deleteVersionId, setDeleteVersionId] = useState<number | null>(null);
  const [availModalOpen, setAvailModalOpen] = useState(false);
  const [availModalType, setAvailModalType] = useState<ScheduleAvailType>('audio');
  const [availModalVersionId, setAvailModalVersionId] = useState<number | null>(null);

  const versionRightsPossible = useAppSelector((state) => selectFocusRightsPossibleById(state, availModalVersionId ?? 0));

  const setScheduleGroups = (groups: ScheduleGroup[]) => {
    dispatch(setScheduleData({ scheduleGroups: groups }));
  };

  const updateGroupRoot = (keyVersionId: number, patch: Partial<Omit<ScheduleRow, 'versionId'>>) => {
    if (!hasReviewChanges) {
      dispatch(setScheduleData({ hasReviewChanges: true }));
    }

    setScheduleGroups(
      scheduleGroups.map((group) => {
        const key = group[0]?.versionId;
        if (key !== keyVersionId) return group;

        const root = group[0];
        const nextRoot: ScheduleRow = { ...root, ...patch };
        return [nextRoot, ...group.slice(1)];
      })
    );
  };

  const removeGroupByKey = (keyVersionId: number) => {
    setScheduleGroups(scheduleGroups.filter((group) => group[0]?.versionId !== keyVersionId));
  };

  const getLanguageLabel = (lang: string) => allLanguagesMap?.[lang.replace('-', '_')]?.label ?? lang;

  const getTerritoryLabel = (ter: string) => {
    const upper = ter.toUpperCase();
    return `${regionNamesInEnglish.of(upper) ?? ter} [${upper}]`;
  };

  const territoryItems = useMemo(
    () =>
      selectedTerritories.map((code) => ({
        label: code,
        value: code
      })),
    [selectedTerritories]
  );

  const dubItems = useMemo(
    () =>
      selectedDubs.map((lang) => ({
        label: `${getLanguageLabel(lang)} [${lang}]`,
        value: lang
      })),
    [selectedDubs]
  );

  const subItems = useMemo(
    () =>
      selectedSubs.map((lang) => ({
        label: `${getLanguageLabel(lang)} [${lang}]`,
        value: lang
      })),
    [selectedSubs]
  );

  const versionItems = useMemo(
    () =>
      selectedVersionIds.map((v) => ({
        label: selectedVersions.find((sv) => sv.id === v)?.alpha_id ?? v.toString(),
        value: v.toString()
      })),
    [selectedVersionIds]
  );

  const handleOpenDelete = (versionId: number) => setDeleteVersionId(versionId);
  const handleCloseDelete = () => setDeleteVersionId(null);

  const handleConfirmDelete = () => {
    if (deleteVersionId == null) return;

    dispatch(removeVersionSelection(deleteVersionId));
    removeGroupByKey(deleteVersionId);
    setDeleteVersionId(null);
  };

  const handleOpenAvailModal = (type: ScheduleAvailType, versionId: number) => {
    setAvailModalType(type);
    setAvailModalVersionId(versionId);
    setAvailModalOpen(true);
  };

  const handleCloseAvailModal = () => {
    setAvailModalOpen(false);
    setAvailModalVersionId(null);
  };

  const handleSaveAvailModal = (next: ScheduleAvailRights) => {
    if (availModalVersionId == null) return;

    dispatch(
      getFocusRightsValidateById({
        title_id: [availModalVersionId],
        license: [selectedLicense],
        availed_platform: selectedPlatforms,
        term_start__gte: startDate,
        term_end__lte: endDate || defaultAvailEndDate,
        territories: next.territories.map((t) => t.toLowerCase()),
        audio: next.audio.map((a) => ({ element_subtype: 'full', language: a })),
        timedtext: next.timedtext.map((t) => ({ element_subtype: 'full-mix', language: t }))
      })
    );

    updateGroupRoot(availModalVersionId, {
      selectedTerritories: next.territories,
      selectedDubs: next.audio,
      selectedSubs: next.timedtext
    });

    setAvailModalOpen(false);
    setAvailModalVersionId(null);
  };

  const availModalInitialSelectedByTab = useMemo(() => {
    if (availModalVersionId == null) {
      return {
        territories: selectedTerritories,
        audio: selectedDubs,
        timedtext: selectedSubs
      };
    }

    const group = scheduleGroups.find((g) => g[0]?.versionId === availModalVersionId);
    const row = group?.[0];

    return {
      territories: row?.selectedTerritories ?? selectedTerritories,
      audio: row?.selectedDubs ?? selectedDubs,
      timedtext: row?.selectedSubs ?? selectedSubs
    };
  }, [availModalVersionId, scheduleGroups, selectedTerritories, selectedDubs, selectedSubs]);

  const deleteTarget =
    deleteVersionId == null
      ? null
      : (() => {
          const version = selectedVersions.find((v) => v.id === deleteVersionId);
          const group = scheduleGroups.find((g) => g[0]?.versionId === deleteVersionId);
          const row = group?.[0];

          if (!version || !row) return null;

          const { date, time } = formatDateTimePartsLocal(row.occurrenceStartDate);
          const meta = version.lineage_meta;
          const title =
            meta?.parent_entity_type === 'movie'
              ? meta?.movie_name
              : `${meta?.series_name} ${typeof meta?.season_sequence_order === 'undefined' ? '' : `S${meta?.season_sequence_order} • `}E${meta?.episode_sequence_order}`;
          const subTitle = meta?.parent_entity_type === 'movie' ? `Series: ${meta?.series_name}` : meta?.episode_name;

          return {
            startDateLabel: `${date} • ${time}`,
            versionType: version.version_type || version.name,
            season: title,
            episodeName: subTitle,
            territoriesLabel: joinOrDash(row.selectedTerritories.map(getTerritoryLabel)),
            subsLabel: joinOrDash(row.selectedSubs.map((sub) => `${getLanguageLabel(sub)} [${sub}]`)),
            dubsLabel: joinOrDash(row.selectedDubs.map((dub) => `${getLanguageLabel(dub)} [${dub}]`)),
            row
          };
        })();

  const deleteDialogScheduleItems: DeleteDialogScheduleItem[] =
    deleteTarget?.row == null
      ? []
      : [
          {
            key: 'territories',
            icon: <PolarisIcon icon="map" color="white" />,
            availCount: deleteTarget.row.selectedTerritories.length,
            items: deleteTarget.row.selectedTerritories.map((code) => ({ label: getTerritoryLabel(code), value: code })),
            selectedValues: deleteTarget.row.selectedTerritories
          },
          {
            key: 'dubs',
            icon: <PolarisIcon icon="music" color="white" />,
            availCount: deleteTarget.row.selectedDubs.length,
            items: deleteTarget.row.selectedDubs.map((code) => ({ label: `${getLanguageLabel(code)} [${code}]`, value: code })),
            selectedValues: deleteTarget.row.selectedDubs
          },
          {
            key: 'subs',
            icon: <PolarisIcon icon="subtitles" color="white" />,
            availCount: deleteTarget.row.selectedSubs.length,
            items: deleteTarget.row.selectedSubs.map((code) => ({ label: `${getLanguageLabel(code)} [${code}]`, value: code })),
            selectedValues: deleteTarget.row.selectedSubs
          }
        ];

  useEffect(() => {
    if (!availModalVersionId) return;

    dispatch(
      getFocusRightsPossible({
        title_id: [availModalVersionId],
        license: [selectedLicense],
        availed_platforms: selectedPlatforms,
        term_start__gte: startDate,
        term_end__lt: endDate || defaultAvailEndDate
      })
    );
  }, [availModalVersionId, selectedLicense, startDate, endDate, selectedPlatforms, dispatch]);

  const getRow = (id: number) => {
    const group = scheduleGroups.find((g) => g[0]?.versionId === id);
    return group ? group[0] : undefined;
  };

  const firstSelectedVersion = selectedVersions[0];
  const headerVersionRow = firstSelectedVersion ? getRow(firstSelectedVersion.id) : undefined;

  return (
    <StyledWrapper>
      <StyledTitle variant="h3">{frequency === 'weekly' ? 'Weekly Schedule' : frequency === 'monthly' ? 'Monthly Schedule' : 'Schedule'}</StyledTitle>
      {firstSelectedVersion && (
        <ReviewScheduleAccordionHeader
          key={`${firstSelectedVersion.display_id}-header`}
          version={firstSelectedVersion}
          frequency={frequency}
          startDate={startDate}
          licence={selectedLicense}
          platforms={selectedPlatforms}
          row={headerVersionRow}
          territoryItems={headerVersionRow?.selectedTerritories.map((code) => ({ label: getTerritoryLabel(code), value: code })) ?? territoryItems}
          dubItems={headerVersionRow?.selectedDubs.map((code) => ({ label: `${getLanguageLabel(code)} [${code}]`, value: code })) ?? dubItems}
          subItems={headerVersionRow?.selectedSubs.map((code) => ({ label: `${getLanguageLabel(code)} [${code}]`, value: code })) ?? subItems}
          versionItems={
            headerVersionRow?.selectedVersions.map((v) => ({
              label: selectedVersions.find((sv) => sv.id === v)?.alpha_id ?? v.toString(),
              value: v.toString()
            })) ?? versionItems
          }
          updateGroupRoot={updateGroupRoot}
          onIncrementalLaunchWorkspaceOpenChange={onIncrementalLaunchWorkspaceOpenChange}
        />
      )}
      <StyledAccordionListContainer>
        {selectedVersions.map((version, index) => {
          const row = getRow(version.id);

          return (
            <ReviewScheduleListAccordion
              key={`${version.display_id}-${index}`}
              index={index}
              version={version}
              frequency={frequency}
              startDate={startDate}
              row={row}
              territoryItems={row?.selectedTerritories.map((code) => ({ label: getTerritoryLabel(code), value: code })) ?? territoryItems}
              dubItems={row?.selectedDubs.map((code) => ({ label: `${getLanguageLabel(code)} [${code}]`, value: code })) ?? dubItems}
              subItems={row?.selectedSubs.map((code) => ({ label: `${getLanguageLabel(code)} [${code}]`, value: code })) ?? subItems}
              versionItems={row?.selectedVersions ?? selectedVersionIds}
              updateGroupRoot={updateGroupRoot}
              onOpenDelete={handleOpenDelete}
              onOpenAvailModal={handleOpenAvailModal}
            />
          );
        })}
      </StyledAccordionListContainer>
      <NewScheduleAvailSelectionModal
        open={availModalOpen}
        onClose={handleCloseAvailModal}
        onSave={handleSaveAvailModal}
        initialTab={availModalType}
        initialSelectedByTab={availModalInitialSelectedByTab}
        rightsPossible={versionRightsPossible}
      />
      <ActionDialog
        open={deleteVersionId != null && deleteTarget != null}
        title="Are you sure you want to delete this schedule:"
        primaryActionLabel="Delete"
        onPrimaryAction={handleConfirmDelete}
        secondaryActionLabel="Cancel"
        onSecondaryAction={handleCloseDelete}
        onClose={handleCloseDelete}
      >
        {deleteTarget && (
          <ReviewScheduleDeleteDialogSummary
            startDateLabel={deleteTarget.startDateLabel}
            season={deleteTarget.season}
            versionType={deleteTarget.versionType}
            episodeName={deleteTarget.episodeName}
            scheduleItems={deleteDialogScheduleItems}
          />
        )}
      </ActionDialog>
    </StyledWrapper>
  );
};

export default ReviewSchedule;

const StyledWrapper = styled('div')`
  background-color: ${({ theme }) => theme.palette.background.default};
`;

const StyledTitle = styled(Typography)`
  font-weight: bold;
  margin-bottom: 24px;
`;

const StyledAccordionListContainer = styled('div')`
  margin-top: ${({ theme }) => theme.spacing(4)};
`;
